/**
 * Notifications Store — manages in-app notifications with caching & polling.
 * Domain: Notifications
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'

const CACHE_DURATION = 5 * 60 * 1000  // 5 minutes
const POLL_INTERVAL  = 10 * 60 * 1000 // 10 minutes

export const useNotificationsStore = defineStore('notifications', () => {
  // ─── State ───────────────────────────────────────────────────────────────
  const items = ref([])
  const loading = ref(false)
  const markingAllRead = ref(false)
  const lastFetchedAt = ref(0)

  // ─── Getters ─────────────────────────────────────────────────────────────
  const unreadCount = computed(() => items.value.filter(n => !n.is_read).length)
  const hasUnread   = computed(() => unreadCount.value > 0)

  // ─── Icon / Color helpers (pure — no side effects) ────────────────────────
  const iconFor = (type) => {
    const map = { meeting: 'mdi-calendar', resolution: 'mdi-gavel', document: 'mdi-file', member: 'mdi-account', system: 'mdi-cog', reminder: 'mdi-bell', alert: 'mdi-alert' }
    return map[type] ?? 'mdi-information'
  }

  const colorFor = (type) => {
    const map = { meeting: 'primary', resolution: 'warning', document: 'info', member: 'success', system: 'grey', reminder: 'orange', alert: 'error' }
    return map[type] ?? 'grey'
  }

  const chipColorFor = (type) => {
    const map = { meeting: 'blue', resolution: 'orange', document: 'green', member: 'purple', system: 'grey', reminder: 'yellow', alert: 'red' }
    return map[type] ?? 'grey'
  }

  const labelFor = (type) => {
    const map = { meeting: 'Meeting', resolution: 'Resolution', document: 'Document', member: 'Member', system: 'System', reminder: 'Reminder', alert: 'Alert' }
    return map[type] ?? 'General'
  }

  const formatTime = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMins = (now - date) / 60000
    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${Math.floor(diffMins)}m ago`
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`
    if (diffMins < 10080) return `${Math.floor(diffMins / 1440)}d ago`
    return date.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    })
  }

  // ─── Actions ─────────────────────────────────────────────────────────────
  const fetch = async (forceRefresh = false) => {
    if (loading.value && !forceRefresh) return
    const cacheAge = Date.now() - lastFetchedAt.value
    if (!forceRefresh && items.value.length > 0 && cacheAge < CACHE_DURATION) return

    loading.value = true
    try {
      const response = await api.get('/notifications', { params: { limit: 20, include_read: true } })
      items.value = response.data.notifications || []
      lastFetchedAt.value = Date.now()
    } catch {
      // Fall back to demo data so UI stays functional
      items.value = _demoNotifications()
    } finally {
      loading.value = false
    }
  }

  const markRead = async (notification) => {
    try { await api.put(`/notifications/${notification.id}/read`) } catch { /* optimistic */ }
    notification.is_read = true
    _touchCache()
  }

  const markUnread = async (notification) => {
    try { await api.put(`/notifications/${notification.id}/unread`) } catch { /* optimistic */ }
    notification.is_read = false
    _touchCache()
  }

  const markAllRead = async () => {
    markingAllRead.value = true
    try { await api.put('/notifications/mark-all-read') } catch { /* optimistic */ }
    items.value.forEach(n => n.is_read = true)
    _touchCache()
    markingAllRead.value = false
  }

  const remove = async (notification) => {
    try { await api.delete(`/notifications/${notification.id}`) } catch { /* optimistic */ }
    const idx = items.value.findIndex(n => n.id === notification.id)
    if (idx !== -1) items.value.splice(idx, 1)
    _touchCache()
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────
  const _touchCache = () => { lastFetchedAt.value = Date.now() }

  const _demoNotifications = () => [
    { id: 1, type: 'meeting', title: 'New Meeting Scheduled', message: 'Board Meeting scheduled for tomorrow at 2:00 PM', created_at: new Date(Date.now() - 2 * 36e5).toISOString(), is_read: false, priority: 'high', action_url: '/meetings/123', action_text: 'View Meeting' },
    { id: 2, type: 'resolution', title: 'New Resolution for Voting', message: 'Resolution "Budget Approval 2024" is now open for voting', created_at: new Date(Date.now() - 4 * 36e5).toISOString(), is_read: false, priority: 'normal', action_url: '/resolutions/456', action_text: 'Cast Vote' },
    { id: 3, type: 'document', title: 'Document Shared', message: 'John Doe shared "Financial Report Q3" with you', created_at: new Date(Date.now() - 864e5).toISOString(), is_read: true, priority: 'normal', action_url: '/documents/789', action_text: 'View Document' },
    { id: 4, type: 'member', title: 'Welcome New Member', message: 'Jane Smith has joined the board', created_at: new Date(Date.now() - 2 * 864e5).toISOString(), is_read: true, priority: 'normal', action_url: '/members/101', action_text: 'View Profile' },
    { id: 5, type: 'system', title: 'System Maintenance', message: 'Scheduled maintenance on Sunday 2:00-4:00 AM', created_at: new Date(Date.now() - 3 * 864e5).toISOString(), is_read: false, priority: 'normal', action_url: null, action_text: null }
  ]

  return {
    // State
    items, loading, markingAllRead, lastFetchedAt,
    // Getters
    unreadCount, hasUnread,
    // Helpers
    iconFor, colorFor, chipColorFor, labelFor, formatTime,
    // Actions
    fetch, markRead, markUnread, markAllRead, remove,
    // Constants (expose for polling)
    POLL_INTERVAL
  }
})
